import { JsonSchema7 } from '@jsonforms/core';
import traverse from 'json-schema-traverse';

// This function generates a default object from a schema
// based on default values in the schema
export function generateDefaultObjectFromSchema(schema: JsonSchema7): unknown {
  const result: Record<string, unknown> = {};

  traverse(schema, {}, (sch, jsonPtr, _root, parentJsonPtr, _parentKeyword, _parentSchema, indexProperty) => {
    if (!jsonPtr.startsWith('/properties/')) return;

    let destination = result;

    if (parentJsonPtr) {
      const pathSegments = parentJsonPtr
        .replace(/\/properties/g, '')
        .split('/')
        .filter(Boolean);

      pathSegments.forEach((segment) => {
        if (typeof destination[segment] !== 'object' || destination[segment] === null) {
          destination[segment] = {};
        }
        destination = destination[segment] as Record<string, unknown>;
      });
    }

    if (indexProperty !== undefined) {
      if (sch.default !== undefined) {
        destination[indexProperty] = sch.default;
      } //If default is not provided in schema, and we are dealing with "object path", we assume empty object as default
      //This is needed, as functions that rely on data generated by this function require default value for object paths to work properly
      else if (sch.type === 'object') {
        destination[indexProperty] = {};
      }
    }
  });

  return result;
}
// This function normalizes the schema object it means that it
// will remove the oneOf and items properties and replace them with the actual value
function normalizeSchemaObject(obj: any): any {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(normalizeSchemaObject);
  }

  if (obj.items) {
    return [normalizeSchemaObject(obj.items)];
  }

  if (obj.oneOf) {
    const combined = Object.values(obj.oneOf).reduce((acc: Record<string, unknown>, value: any) => {
      return { ...acc, ...value };
    }, {});

    return normalizeSchemaObject(combined);
  }

  return Object.keys(obj).reduce((acc: Record<string, unknown>, key: string) => {
    acc[key] = normalizeSchemaObject(obj[key]);
    return acc;
  }, {});
}

export const generateDefaultFormData = (schema: JsonSchema7): Record<string, unknown> => {
  const generatedDefaults = generateDefaultObjectFromSchema(schema);
  const normalizedDefaults = normalizeSchemaObject(generatedDefaults);

  return normalizedDefaults;
};
